import React, { Fragment, useEffect, useState } from 'react';
import {
	Routes,
	Route,
	useNavigate,
	useParams,
	Outlet,
	Navigate,
} from 'react-router-dom';
import moment from 'moment';
import { Menu, Transition } from '@headlessui/react';
import { FolderIcon } from '@heroicons/react/20/solid';
import {
	CheckCircleIcon,
	ChevronLeftIcon,
	ClockIcon,
	ExclamationTriangleIcon,
	FingerPrintIcon,
	MinusCircleIcon,
	MinusIcon,
	PlusCircleIcon,
	PlusIcon,
	RectangleGroupIcon,
	Squares2X2Icon,
	XCircleIcon,
	XMarkIcon,
	QueueListIcon,
	MagnifyingGlassIcon,
	Bars3Icon,
	UsersIcon,
	UserGroupIcon,
	DocumentIcon,
	Cog6ToothIcon,
	MapPinIcon,
} from '@heroicons/react/24/outline';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

import {
	archiveAsset,
	getAsset,
	getAssets,
	getLocation,
	getLocations,
	getTickets,
	updateAsset,
	getManufacturers,
	getModels,
} from '../../../api';
import { getTeam } from '../../../api/company';
import { getAllPowerUsers } from '../../../api/power-users';
import { addToWatchlist } from '../../../api/watchlist';
import { getModuleCategories } from '../../../api/modules';

import useModal from '../../../hooks/useModal';
import usePrevious from '../../../hooks/usePrevious';
import useAsync from '../../../hooks/useAsync';
import useSettings from '../../../hooks/useSettings';

import LoadingSkeleton from '../../../components/Shell/LoadingSkeleton';
import { MainContent, PageHeading } from '../../../components/Shell/Content';

import {
	classNames,
	createDataTree,
	getUserFullName,
	objectAccess,
	pluralize,
} from '../../../components/helpers/functions';

import ProtectedRoute from '../../../components/helpers/ProtectedRoute';
import Table from '../../../components/DataTable/Table_old';
import Badge from '../../../components/Badge';
import { AvatarGenerated } from '../../../components/Avatar';
import { Button } from '../../../components/Button';
import {
	AttributeRow,
	TemplateDropdown,
	TemplateField,
	TemplateReactSelect,
} from '../../../components/Attributes/Attributes_old';
import Modal from '../../../components/Shell/Modal';

import { Base, Content, NoAssetsPage } from '../../../components/Shell/Content';

import Overview from './Overview';
import Documents from './Documents';
import Vendors from './Vendors';
import Contracts from './Contracts';
import Watchlist from './Watchlist';
import Settings from './Settings';
import Timeline from './Timeline';

import ReactSelect from 'react-select';
import { getTags } from '../../../api/tags';
import Banner from '../../../components/Banner';
import QuickAddTag from '../../../modals/QuickAddTag';
import { parseAssetTemplate } from '../../../components/nextgen/AssetSelectorBeta';
import { TextStack, StackableModal } from '../../../components/nextgen';
import { TemplateCreateable } from '../../../components/Attributes/Attributes_old';

const DOCUMENTS_MODULE_ID = process.env.REACT_APP_MODULE_ID_DOCUMENTS;

const commonColumns = [
	{
		title: 'Status',
		dataIndex: 'online',
		key: 'status',
		render: (online, data) => {
			const hasIssues = data?.tickets?.filter(
				(ticket) => ticket.active,
			)?.length;

			const isStable = online && !hasIssues;

			return isStable ? (
				<Badge status="success" noBackground>
					Stable
				</Badge>
			) : (
				<Badge status={hasIssues ? 'error' : 'default'} noBackground>
					{`${online ? 'Online' : 'Offline'}${
						hasIssues
							? ` with ${hasIssues} issue${hasIssues > 1 ? 's' : ''}`
							: ''
					}`}
				</Badge>
			);
		},
	},
	{
		title: 'Asset',
		dataIndex: 'asset',
		key: 'status',
		render: (id, row) => {
			return row ? (
				<>
					<div className="font-medium">
						{`${row?.manufacturerName || row?.manufacturer?.name} ${
							row?.modelName || row?.model?.name
						}`}
					</div>
					<div className="text-gray-400">{`S/N: ${row?.serialNumber}`}</div>
				</>
			) : (
				'--'
			);
		},
	},
	{
		title: 'Tag',
		dataIndex: 'tag',
		key: 'tag',
	},
	{
		title: 'Serial Number',
		dataIndex: 'serialNumber',
		key: 'serial-number',
	},
	{
		title: 'Location',
		dataIndex: 'location',
		key: 'location',
		render: (location) => location?.name || 'Not set',
	},
];

const LocationList = () => {
	const navigate = useNavigate();
	const { id } = useParams();

	useEffect(() => {
		document.title = 'new title';
	}, []);
	const { value: locations, execute: refreshLocations } =
		useAsync(getLocations);
	const { value: location, execute: refreshLocation } = useAsync(getLocation, {
		defaultPayload: { id },
	});
	const { value: items, execute: refreshAssets } = useAsync(getAssets, {
		defaultPayload: { locationId: id },
	});

	useEffect(() => {
		refreshLocations();
		refreshLocation({ id });
		refreshAssets({ locationId: id });
	}, [id]);

	const locationChildren = locations?.rows?.filter(
		(location) => location.parent_id === id,
	);

	let pages = [];

	const getParentLocation = (id) => {
		if (!id && locations) {
			return;
		}

		const temp = locations?.rows?.find((o) => o.id === id);
		if (temp) {
			pages.push({
				name: temp.name,
				path: `/assets/all?location=${temp.id}`,
				current: !temp?.parent_id,
			});
			return temp?.parent_id && getParentLocation(temp?.parent_id);
		} else {
			return null;
		}
	};

	getParentLocation(location?.id);

	return (
		<Base>
			<PageHeading pages={pages.reverse()} divider={false} homePath="/assets">
				Assets
			</PageHeading>
			<Content>
				{locationChildren?.length ? (
					<ul
						role="list"
						className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 xl:grid-cols-3"
					>
						{locationChildren?.map((location) => (
							<li
								key={location.id}
								className="relative col-span-1 flex rounded-md shadow-sm cursor-pointer"
								onClick={() => navigate(`/assets/all?location=${location.id}`)}
							>
								<div
									className={
										'bg-red-400 flex-shrink-0 flex items-center justify-center w-16 text-white text-sm font-medium rounded-l-md'
									}
								>
									<FolderIcon className="w-6 h-6 text-white" />
								</div>
								<div className="flex flex-1 items-center justify-between truncate rounded-r-md border-t border-r border-b border-gray-200 bg-white">
									<div className="flex-1 truncate px-6 py-4 text-sm">
										<a className="font-medium text-gray-900 hover:text-gray-600">
											{location.name}
										</a>
									</div>
									<div className="flex-shrink-0 mr-2">
										<ChevronRightIcon
											className="h-5 w-5 text-gray-400"
											aria-hidden="true"
										/>
									</div>
								</div>
							</li>
						))}
					</ul>
				) : null}

				<Table
					columns={commonColumns}
					dataSource={items}
					onRow={(row) => navigate(`/assets/${row.id}/overview`)}
					actions={[
						{
							name: 'Report Issue',
							button: true,
							type: 'primary',
							onClick: (row) => navigate(`/tickets/create?assetId=${row.id}`),
						},
					]}
					emptyTableText="No items at this location"
				/>
			</Content>
		</Base>
	);
};

const InventoryList = () => {
	const navigate = useNavigate();
	const { value: items, status } = useAsync(getAssets, {
		defaultPayload: {
			missingLocation: true,
		},
	});

	const { value: locations } = useAsync(getLocations);

	const locationTree = createDataTree(locations?.rows);

	const finalLocationTree = locationTree;

	return (
		<Base>
			<PageHeading divider={false}>Assets</PageHeading>
			<Content loading={status === 'pending'} error={status === 'error'}>
				<ul
					role="list"
					className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 xl:grid-cols-3"
				>
					{finalLocationTree.map((location) => (
						<li
							key={location.id}
							className="relative col-span-1 flex rounded-md shadow-sm cursor-pointer"
							onClick={() => navigate(`/assets/all?location=${location.id}`)}
						>
							<div
								className={
									'bg-red-400 flex-shrink-0 flex items-center justify-center w-16 text-white text-sm font-medium rounded-l-md'
								}
							>
								<FolderIcon className="w-6 h-6 text-white" />
							</div>
							<div className="flex flex-1 items-center justify-between truncate rounded-r-md border-t border-r border-b border-gray-200 bg-white">
								<div className="flex-1 truncate px-6 py-4 text-sm">
									<a className="font-medium text-gray-900 hover:text-gray-600">
										{location.name}
									</a>
								</div>
								<div className="flex-shrink-0 mr-2">
									<ChevronRightIcon
										className="h-5 w-5 text-gray-400"
										aria-hidden="true"
									/>
								</div>
							</div>
						</li>
					))}
				</ul>

				<h2 class="mt-10 text-lg font-medium leading-6 text-gray-900">
					No location set
				</h2>

				<Table
					columns={commonColumns}
					dataSource={items}
					onRow={(row) => navigate(`/assets/${row.id}/overview`)}
					emptyTableText="No items at this location"
				/>
			</Content>
		</Base>
	);
};

const EditCoreModal = ({ open, onClose, item, onUpdate, isUpdating }) => {
	const [form, setForm] = useState({
		manufacturer: item?.manufacturer?.id
			? {
					value: item.manufacturer.id,
					label: item.manufacturer.name,
					isNew: false,
				}
			: item?.manufacturerName
				? {
						value: item.manufacturerName,
						label: item.manufacturerName,
						isNew: true,
					}
				: null,
		model: item?.model?.id
			? { value: item.model.id, label: item.model.name, isNew: false }
			: item?.modelName
				? { value: item.modelName, label: item.modelName, isNew: true }
				: null,
	});

	const {
		value: manufacturers,
		execute: onRefreshManufacturers,
		status: isLoadingManufacturers,
	} = useAsync(getManufacturers, { immediate: false });

	const {
		value: models,
		execute: onRefreshModels,
		status: isLoadingModels,
	} = useAsync(getModels, { immediate: false });

	// Load manufacturers on mount
	useEffect(() => {
		if (open) {
			onRefreshManufacturers();
		}
	}, [open]);

	// Load models when manufacturer changes
	useEffect(() => {
		if (open && form.manufacturer?.value && !form.manufacturer?.isNew) {
			onRefreshModels({ manufacturerId: form.manufacturer.value });
		}
	}, [open, form.manufacturer?.value, form.manufacturer?.isNew]);

	// Reset form when item changes or modal opens
	useEffect(() => {
		if (open && item) {
			setForm({
				manufacturer: item?.manufacturer?.id
					? {
							value: item.manufacturer.id,
							label: item.manufacturer.name,
							isNew: false,
						}
					: item?.manufacturerName
						? {
								value: item.manufacturerName,
								label: item.manufacturerName,
								isNew: true,
							}
						: null,
				model: item?.model?.id
					? { value: item.model.id, label: item.model.name, isNew: false }
					: item?.modelName
						? { value: item.modelName, label: item.modelName, isNew: true }
						: null,
			});
		}
	}, [open, item?.id]);

	const manufacturerOptions = (() => {
		const opts =
			manufacturers?.rows?.map((m) => ({
				label: m.name,
				value: m.id,
				key: m.id,
			})) || [];
		// Include current selection if not in list (handles loading state)
		if (
			form.manufacturer?.value &&
			form.manufacturer?.label &&
			!form.manufacturer?.isNew
		) {
			const exists = opts.some((o) => o.value === form.manufacturer.value);
			if (!exists) {
				opts.unshift({
					label: form.manufacturer.label,
					value: form.manufacturer.value,
					key: form.manufacturer.value,
				});
			}
		}
		return opts;
	})();

	const modelOptions = (() => {
		if (!form.manufacturer?.value || form.manufacturer?.isNew) return [];
		const opts =
			models?.rows?.map((m) => ({
				label: m.name,
				value: m.id,
				key: m.id,
			})) || [];
		// Include current selection if not in list (handles loading state)
		if (form.model?.value && form.model?.label && !form.model?.isNew) {
			const exists = opts.some((o) => o.value === form.model.value);
			if (!exists) {
				opts.unshift({
					label: form.model.label,
					value: form.model.value,
					key: form.model.value,
				});
			}
		}
		return opts;
	})();

	const handleManufacturerChange = (dataIndex, value, option) => {
		setForm({
			manufacturer: value
				? {
						value,
						label: option?.label || value,
						isNew: option?.__isNew__ || false,
					}
				: null,
			model: null, // Clear model when manufacturer changes
		});
	};

	const handleModelChange = (dataIndex, value, option) => {
		setForm((prev) => ({
			...prev,
			model: value
				? {
						value,
						label: option?.label || value,
						isNew: option?.__isNew__ || false,
					}
				: null,
		}));
	};

	const handleSave = () => {
		const payload = {
			id: item.id,
		};

		// Handle manufacturer
		if (form.manufacturer?.isNew) {
			payload.manufacturerName = form.manufacturer.value;
		} else if (form.manufacturer?.value) {
			payload.manufacturerId = form.manufacturer.value;
		}

		// Handle model
		if (form.model?.isNew) {
			payload.model = form.model.value;
		} else if (form.model?.value) {
			payload.modelId = form.model.value;
		}

		onUpdate(payload, () => {
			onClose();
		});
	};

	const canSave =
		form.manufacturer?.value && (form.model?.value || form.manufacturer?.isNew);
	const modelDisabled = !form.manufacturer?.value;

	return (
		<StackableModal
			open={open}
			onClose={onClose}
			title="Edit Manufacturer & Model"
			size="md"
		>
			<div className="space-y-4 mt-4">
				<TemplateCreateable
					name="Manufacturer"
					dataIndex="manufacturer"
					value={form.manufacturer?.value || ''}
					onChange={handleManufacturerChange}
					options={manufacturerOptions}
					placeholder="Select or create manufacturer..."
					required
					span=""
				/>

				<TemplateCreateable
					name="Model"
					dataIndex="model"
					value={form.model?.value || ''}
					onChange={handleModelChange}
					options={modelOptions}
					placeholder={
						modelDisabled
							? 'Select a manufacturer first...'
							: form.manufacturer?.isNew
								? 'Enter new model name...'
								: 'Select or create model...'
					}
					disabled={modelDisabled}
					span=""
				/>

				{form.manufacturer?.isNew && (
					<p className="text-xs text-gray-500">
						Creating a new manufacturer. Enter a model name for this
						manufacturer.
					</p>
				)}

				<div className="flex justify-end space-x-3 pt-4 border-t">
					<button
						type="button"
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
						onClick={onClose}
					>
						Cancel
					</button>
					<button
						type="button"
						className={classNames(
							'px-4 py-2 text-sm font-medium text-white rounded-md',
							canSave && !isUpdating
								? 'bg-indigo-600 hover:bg-indigo-700'
								: 'bg-indigo-400 cursor-not-allowed',
						)}
						onClick={handleSave}
						disabled={!canSave || isUpdating}
					>
						{isUpdating ? 'Saving...' : 'Save Changes'}
					</button>
				</div>
			</div>
		</StackableModal>
	);
};

export default () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const { open, setClose } = useModal('assetModalForm');
	const {
		open: isNearbyAssetSliderOpen,
		setOpen: openNearbyAssetsSlider,
		setClose: closeNearbyAssetsSlider,
	} = useModal('nearbyAssets');

	const { setOpen: openTagModal } = useModal('addTagModal');
	const [search, setSearch] = useState('');
	const [isEditCoreModalOpen, setIsEditCoreModalOpen] = useState(false);

	const { setOpen: openDeleteModal, setClose: closeDeleteModal } =
		useModal('deleteModal');

	const settings = useSettings();

	const assetDisplayTemplate = settings.get('assetDisplay');

	const {
		value: item,
		execute: refreshItem,
		status,
	} = useAsync(getAsset, {
		immediate: false,
	});

	const { execute: onArchiveAsset } = useAsync(archiveAsset, {
		immediate: false,
	});

	const {
		execute: onUpdateAsset,
		status: isUpdatingAsset,
		error: hasUpdatingError,
	} = useAsync(updateAsset, {
		forceCallback: true,
		immediate: false,
		showNotification: { success: 'Asset has been updated', error: 'raw' },
	});

	const { value: documentCategories } = useAsync(getModuleCategories, {
		defaultPayload: { id: DOCUMENTS_MODULE_ID },
		silent: true,
	});

	const { value: assetTickets, execute: onRefreshTickets } = useAsync(
		getTickets,
		{
			defaultPayload: {
				assetId: id,
				status: ['incoming', 'inprogress'],
			},
			silent: true,
		},
	);

	const {
		value: allAssets,
		status: isLoadingAllAssets,
		execute: onRefreshAssets,
	} = useAsync(getAssets, { silent: true });

	useEffect(() => {
		if (id) {
			refreshItem({ id });
			onRefreshTickets({
				assetId: id,
				status: ['incoming', 'inprogress'],
			});

			if (open) {
				setClose();
			}
		}
	}, [id]);

	const activeContracts = item?.contracts?.filter(
		(contract) => contract?.active && moment().isBefore(contract?.endDate),
	);

	const groupedTickets = assetTickets?.rows?.reduce(
		(prev, cur) => {
			if (cur?.status === 'incoming') {
				prev.incoming.push(cur);
			} else if (cur?.status === 'inprogress') {
				prev.inprogress.push(cur);
			}

			return prev;
		},
		{ incoming: [], inprogress: [] },
	);

	const { incoming: incomingTickets = [], inprogress: inprogressTickets = [] } =
		groupedTickets || {};

	const banners = [
		...(incomingTickets?.length
			? [
					{
						message: (
							<>
								<div className="flex items-center">
									<div className="flex-shrink-0">
										<ExclamationTriangleIcon
											className="h-6 w-6 text-orange-500"
											aria-hidden="true"
										/>
									</div>
									<div className="ml-3">
										<p
											className={classNames(
												'text-sm font-light',
												'text-orange-800',
											)}
										>
											<div>
												<>
													<span className="font-semibold">Caution:</span>
													<span className="font-normal pl-1.5">
														{`There ${pluralize(
															incomingTickets?.length,
															'is',
															'are',
														)} ${incomingTickets?.length} incoming ${pluralize(
															incomingTickets?.length,
															'ticket',
															'tickets',
														)} for this asset`}
													</span>
												</>
											</div>
										</p>
									</div>
								</div>
							</>
						),
						status: 'warning',
						onClick: () =>
							navigate(`/tickets-beta?assetId=${item?.id}&status=active`),
					},
				]
			: []),
		...(inprogressTickets?.length
			? [
					{
						message: (
							<>
								<div className="flex items-center">
									<div className="flex-shrink-0">
										<ExclamationTriangleIcon
											className="h-6 w-6 text-red-500"
											aria-hidden="true"
										/>
									</div>
									<div className="ml-3">
										<p
											className={classNames(
												'text-sm font-light',
												'text-red-800',
											)}
										>
											<div>
												<>
													<span className="font-semibold">Warning:</span>
													<span className="font-normal pl-1.5">
														{`There ${pluralize(
															inprogressTickets?.length,
															'is',
															'are',
														)} ${inprogressTickets?.length} open ${pluralize(
															inprogressTickets?.length,
															'ticket',
															'tickets',
														)} for this asset`}
													</span>
												</>
											</div>
										</p>
									</div>
								</div>
							</>
						),
						status: 'error',
						onClick: () =>
							navigate(`/tickets-beta?assetId=${item?.id}&status=active`),
					},
				]
			: []),
	];

	return (
		<>
			{status === 'pending' ? (
				<LoadingSkeleton />
			) : item ? (
				<>
					<div className="max-w-(--breakpoint-xl) flex mx-auto">
						<div className="p-8 pt-12 pb-0 flex-1">
							<div className="block">
								<div className="pb-6 mb-4 sm:flex sm:items-center justify-between pb-0 mb-0">
									<div className="flex items-center justify-between">
										<div>
											<div className="text-xl font-semibold">
												<div className="flex items-center">
													<div className="flex items-center">
														<div
															className=" rounded-lg py-4 mr-4 cursor-pointer flex items-center text-sm text-gray-600 "
															onClick={() => {
																if (window.history.state?.idx > 0) {
																	navigate(-1);
																} else {
																	navigate('/assets/all');
																}
															}}
														>
															<ChevronLeftIcon className="w-4 h-4 text-gray-500 mr-3 font-semibold" />{' '}
														</div>
													</div>

													<div>
														<div>
															{`${item?.manufacturerName || item?.manufacturer?.name} ${
																item?.modelName || item?.model?.name
															}`}
														</div>
														<div className="font-normal text-gray-600 text-sm">
															{assetDisplayTemplate
																? parseAssetTemplate(assetDisplayTemplate, item)
																: `${item?.location?.name || 'No location'} · SN: ${item?.serialNumber || 'N/A'}`}
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>

									<div className="hidden sm:block">
										<div className="flex items-center justify-end">
											<div className="flex items-center">
												<nav
													className="-mb-px flex space-x-4 lg:space-x-8 mr-2 md:mr-4"
													aria-label="Tabs"
												>
													{[
														{
															name: 'Manage',
															key: 'settings',
															icon: Cog6ToothIcon,
														},
														{
															name: 'Nearby Assets',
															key: 'nearby',
															icon: MapPinIcon,
															onClick: () =>
																isNearbyAssetSliderOpen
																	? closeNearbyAssetsSlider()
																	: openNearbyAssetsSlider(),
														},
													].map((overviewTab) => {
														const current = false
														const TabIcon = overviewTab?.icon;

														if (overviewTab?.key === 'settings') {
															return (
																<Menu
																	as="div"
																	className="relative inline-block text-left"
																>
																	<div className="flex">
																		<Menu.Button className="focus:outline-none">
																			<div
																				className={classNames(
																					false
																						? 'border-indigo-500 text-indigo-600'
																						: 'border-transparent text-gray-500 hover:text-gray-700',
																					'flex whitespace-nowrap border-b-2 py-4 px-1 text-sm font-normal cursor-pointer items-center',
																				)}
																			>
																				<div className="block xl:hidden flex items-center">
																					{overviewTab?.icon ? (
																						<TabIcon className="w-6 h-6" />
																					) : null}
																				</div>

																				<div className="hidden xl:flex items-center">
																					{overviewTab?.icon ? (
																						<TabIcon className="w-4 h-4 mr-2" />
																					) : null}
																					{overviewTab.name}
																				</div>
																			</div>
																		</Menu.Button>
																	</div>

																	<Transition
																		as={Fragment}
																		enter="transition ease-out duration-100"
																		enterFrom="transform opacity-0 scale-95"
																		enterTo="transform opacity-100 scale-100"
																		leave="transition ease-in duration-75"
																		leaveFrom="transform opacity-100 scale-100"
																		leaveTo="transform opacity-0 scale-95"
																	>
																		<Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
																			<div className="py-1">
																				<Menu.Item>
																					{({ active }) => (
																						<div
																							className={classNames(
																								'hover:bg-gray-50 text-gray-900 block px-4 py-2 text-xs cursor-pointer',
																							)}
																							onClick={() =>
																								setIsEditCoreModalOpen(true)
																							}
																						>
																							Admin:Edit Core
																						</div>
																					)}
																				</Menu.Item>
																				<Menu.Item>
																					{({ active }) => (
																						<div
																							className={classNames(
																								'hover:bg-red-50 text-red-900 block px-4 py-2 text-xs cursor-pointer',
																							)}
																							onClick={() =>
																								openDeleteModal({
																									title:
																										'Are you sure you want to delete this asset?',
																									description:
																										'You will have 30 days to undo this asset.  You can access assets scheduled to be permanently from the App Settings page',
																									confirmText: 'Delete Asset',
																									onConfirm: () =>
																										onArchiveAsset(
																											{ id: item?.id },
																											() => {
																												closeDeleteModal();
																												navigate('/assets');
																											},
																										),
																								})
																							}
																						>
																							Delete Asset
																						</div>
																					)}
																				</Menu.Item>
																			</div>
																		</Menu.Items>
																	</Transition>
																</Menu>
															);
														}
														return (
															<div
																key={overviewTab.name}
																onClick={() =>
																	typeof overviewTab.onClick === 'function'
																		? overviewTab?.onClick()
																		: {}
																}
																className={classNames(
																	current
																		? 'border-indigo-500 text-indigo-600'
																		: 'border-transparent text-gray-500 hover:text-gray-700',
																	'flex whitespace-nowrap border-b-2 py-4 px-1 text-sm font-normal cursor-pointer items-center',
																)}
																aria-current={current ? 'page' : undefined}
															>
																<div className="block xl:hidden flex items-center">
																	{overviewTab?.icon ? (
																		<TabIcon className="w-6 h-6 mr-2" />
																	) : null}
																</div>

																<div className="hidden xl:flex items-center">
																	{overviewTab?.icon ? (
																		<TabIcon className="w-4 h-4 mr-2" />
																	) : null}
																	{overviewTab.name}
																</div>
															</div>
														);
													})}
												</nav>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-gray-50">
						{banners?.length ? (
							<div className="mb-4">
								{banners?.map(({ status, message, onClick, size = 'sm' }) => (
									<Banner status={status} onClick={onClick} size={size}>
										{message}
									</Banner>
								))}
							</div>
						) : null}

						<div className="max-w-(--breakpoint-xl) flex mx-auto">
							<div className="p-8 pb-12 flex-1 pt-0">
							
								<div className="hidden sm:block"><Overview
					item={item}
					refreshAllAssets={onRefreshAssets}
					refreshItem={refreshItem}
					openTagModal={openTagModal}
				/></div>
									
							</div>
						</div>
					</div>
				</>
			) : (
				<>
					<NoAssetsPage />
				</>
			)}

			<EditCoreModal
				open={isEditCoreModalOpen}
				onClose={() => setIsEditCoreModalOpen(false)}
				item={item}
				onUpdate={(payload, callback) => {
					onUpdateAsset(payload, () => {
						refreshItem({ id });
						callback?.();
					});
				}}
				isUpdating={isUpdatingAsset === 'pending'}
			/>
			<QuickAddTag />
		</>
	);
};

